import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';

import { Store } from '@ngrx/store';
import { RootState } from '@store/index';
import { RouterActions } from '@store/router';

import {
  ContentBasicPageService,
  NodePageData,
  NodePageDataAttributes,
  ParagraphAccordionData,
  ParagraphCtaData,
  ParagraphDividerData,
  ParagraphFormContentData,
  ParagraphFreeTextData,
  ParagraphHeroSliderData,
  ParagraphImageData,
  ParagraphMemberData,
  ParagraphMultipleColumnData,
  ParagraphRelatedItemsData,
  ParagraphRelatedSolutionsData,
  ParagraphSectionHeadlineData,
  ParagraphSliderData,
  ParagraphTeamData,
  ParagraphTestimonialSliderData,
  ParagraphTextMarginalData,
  ParagraphTextMediaBoxData,
  ParagraphWidgetData,
  ParagraphSmallColumnModuleData,
  ParagraphLogoSliderData,
  ParagraphNavigationWidgetData,
  ParagraphNewsTickerItemData
} from '@generated/content';
import { NodeRouter } from '@generated/router';

import { CmsComponentConfig, TCustomCmsPageType } from '@interfaces/config';
import { CmsComponentNodeData } from '@interfaces/cms-component-node-data';
import { CustomCmsPage } from '@interfaces/config/core/custom-cms-page';
import { Breadcrumb, MediaDataUrls, TSpsSeo } from '@models/interfaces';

import { CmsRestApiService } from '@services/api/cms-rest-api/cms-rest-api.service';
import { EnvironmentService } from '@services/environment/environment.service';
import { SpsLogger } from '@lib/utils';
import { LogoSliderComponentResolver } from '@services/resolvers/component-resolvers/logo-slider-component/logo-slider-component-resolver';
import { NavigationWidgetComponentResolver } from '@services/resolvers/component-resolvers/navigation-widget-component/navigation-widget-component-resolver';
import {NewsTickerComponentResolver} from '@services/resolvers/component-resolvers/news-ticker-component/news-ticker-component-resolver';
import { AccordionComponentResolver } from '@services/resolvers/component-resolvers/accordion-component/accordion-component-resolver';
import { BaseComponentResolver } from '@services/_base/base-component-resolver/base-component-resolver';
import { CtaComponentResolver } from '@services/resolvers/component-resolvers/cta-component/cta-component-resolver';
import { DividerComponentResolver } from '@services/resolvers/component-resolvers/divider-component/divider-component-resolver.service';
import { FormComponentResolver } from '@services/resolvers/component-resolvers/form-component/form-component-resolver';
import { FreeTextComponentResolver } from '@services/resolvers/component-resolvers/free-text-component/free-text-component-resolver';
import { HeroSliderComponentResolver } from '@services/resolvers/component-resolvers/hero-slider-component/hero-slider-component-resolver';
import { MediaResolver } from '@services/resolvers/auxiliary/media-resolver';
import { MediaComponentResolver } from '@services/resolvers/component-resolvers/media-component/media-component-resolver';
import { MemberComponentResolver } from '@services/resolvers/component-resolvers/member-component/member-component-resolver';
import { MediaSliderComponentResolver } from '@services/resolvers/component-resolvers/media-slider-component/media-slider-component-resolver';
import { MultipleColumnComponentResolver } from '@services/resolvers/component-resolvers/multiple-column-component/multiple-column-component-resolver';
import { RelatedItemsComponentResolver } from '@services/resolvers/component-resolvers/related-items-component/related-items-component-resolver';
import { RelatedSolutionsComponentResolver } from '@services/resolvers/component-resolvers/related-solutions-component/related-solutions-component-resolver';
import { SectionHeadlineComponentResolver } from '@services/resolvers/component-resolvers/section-headline-component/section-headline-component-resolver';
import { TeamComponentResolver } from '@services/resolvers/component-resolvers/team-component/team-component-resolver';
import { TestimonialSliderComponentResolver } from '@services/resolvers/component-resolvers/testimonial-slider-component/testimonial-slider-component-resolver';
import { TextMarginalComponentResolver } from '@services/resolvers/component-resolvers/text-marginal-component/text-marginal-component-resolver';
import { TextMediaBoxComponentResolver } from '@services/resolvers/component-resolvers/text-media-box-component/text-media-box-component-resolver';
import { WidgetComponentResolver } from '@services/resolvers/component-resolvers/widget-component/widget-component-resolver';
import { SmallColumnModuleComponentResolver } from '@services/resolvers/component-resolvers/small-column-module-component/small-column-module-component-resolver';

import { LANGUAGES, LanguagesEnum } from '@core/config';

export interface ComponentResolver extends BaseComponentResolver {}

interface RecognizedComponentData {
  resolver: ComponentResolver;
  node: CmsComponentNodeData;
  nodePageData: CmsComponentNodeData[];
}

@Injectable({ providedIn: 'root' })
export class CmsComponentsResolver {
  constructor(
    private store: Store<RootState>,
    private contentBasicPageService: ContentBasicPageService,
    private cmsRestApiService: CmsRestApiService,
    private mediaResolver: MediaResolver
  ) {}

  private redirections: string[] = [];

  public async resolveComponents(path: string = null): Promise<{
    cacheKey: string;
    title: string;
    breadcrumbs: Breadcrumb[];
    newsletterCta: boolean;
    cmsPageType: TCustomCmsPageType;
    componentConfigs: CmsComponentConfig<any>[];
    seo: TSpsSeo;
  }> | null {
    if (path.endsWith('.map')) {
      return null;
    }

    let translatePath: NodeRouter;

    try {
      translatePath = await this.cmsRestApiService.getTranslatePath(path);
    } catch (e) {
      return Promise.reject(e);
    }

    if (translatePath?.redirect?.length) {
      const [redirect] = translatePath.redirect;

      this.redirections.push(redirect.to);
      this.redirections.splice(-3);

      // Redirection loop
      if (this.redirections.at(-3) === redirect.to) {
        SpsLogger.warn(`[CMS COMPONENTS RESOLVER]: redirections loop detected: ${this.redirections.join(' -> ')} `);
        this.store.dispatch(RouterActions.navigate({ url: '/site/404' }));
        return null;
      }

      this.store.dispatch(RouterActions.navigate({ url: redirect.to }));

      return null;
    }

    const pageContent = await this.getPageContent(translatePath.entity.uuid);
 

    if (!pageContent.included) {
      return Promise.reject('Page content is missing included attribute.');
    }

    const { field_breadcrumb, field_newsletter, title } = pageContent.data.attributes;
    const breadcrumbsEnabled = field_breadcrumb === NodePageDataAttributes.FieldBreadcrumbEnum.Yes;
    const newsletterCtaEnabled = field_newsletter === NodePageDataAttributes.FieldNewsletterEnum.True;

    const breadcrumbs: Breadcrumb[] = (pageContent.data.attributes.breadcrumbs ?? []).map(crumb => ({
      href: crumb.url,
      label: crumb.title,
    }));

    const cacheKey = btoa(pageContent.data.id + pageContent.data.attributes.changed);
    const langcode = pageContent.data?.attributes?.langcode;
    const language = LANGUAGES.find(lang => langcode?.toUpperCase() === lang.value)?.value || LanguagesEnum.EN;
    const seo: TSpsSeo = {
      ...pageContent.data.attributes.field_seo_basicpage,
      hrefLang: pageContent?.data?.attributes?.field_hreflang_url ?? null,
      language,
    };
    const cmsPageType: TCustomCmsPageType = pageContent.data.type === NodePageData.TypeEnum.NodePage ? 'NODE_PAGE' : 'BLOG_PAGE';

    const componentConfigs = this.resolveComponentConfigs(pageContent.included);

    if (pageContent.data.relationships?.field_teaser_image_page?.data?.id) {
      try {
        const imageConfig = await this.mediaResolver.getImageConfig(
          pageContent.data.relationships?.field_teaser_image_page.data.id
        );
        const dataUrls = (imageConfig.dataUrls || {}) as MediaDataUrls;
        const teaserImage = dataUrls['small_600w'] || dataUrls['xsmall_400w'] || dataUrls['thumbnail_200w'];

        seo.image_src = teaserImage ?? null;
      } catch (e) {
        SpsLogger.warn(
          `[CMS COMPONENTS RESOLVER] error trying to resolve teaser image: ${JSON.stringify(
            pageContent.data.relationships.field_teaser_image_page
          )}`
        );
      }
    }

    return {
      cacheKey,
      title,
      breadcrumbs: breadcrumbsEnabled ? breadcrumbs : [],
      newsletterCta: newsletterCtaEnabled,
      cmsPageType,
      componentConfigs,
      seo,
    };
  }

  private getPageContent(uuid: string): Promise<CustomCmsPage> {
    const rel = EnvironmentService.isProdLive ? 'latest-version' : 'working-copy';
    return firstValueFrom(this.contentBasicPageService.nodePageEntityGet(uuid, `rel:${rel}`) as Observable<CustomCmsPage>);
  }

  private resolveComponentConfigs(nodePageData: CmsComponentNodeData[] = null): CmsComponentConfig<any>[] | null {
    if (!nodePageData) {
      return null;
    }

    // Remove hidden paragraphs
    // TODO: check if "pragraph" is a typo here or in CMS
    const filteredData = nodePageData.filter(node => node.attributes?.['field_hide_section_pragraph'] !== true);

    const recognizedComponentData: RecognizedComponentData[] = filteredData.reduce((agg, node) => {
      const resolver = CmsComponentsResolver.getComponentResolver(node.type);
   
      return resolver ? [...agg, { resolver, node, nodePageData }] : agg;

    }, []);

    return recognizedComponentData.reduce((configs, { resolver, node, nodePageData }) => {
      const componentConfig = resolver.buildComponentConfig<typeof resolver.resolveComponentConfig>(node, nodePageData);
      return componentConfig ? [...configs, componentConfig] : configs;
    }, []);
  }

  private static getComponentResolver(componentType): ComponentResolver {


    switch (componentType) {

       
      
      case ParagraphAccordionData.TypeEnum.ParagraphAccordion:
        return AccordionComponentResolver.getInstance();
      case ParagraphCtaData.TypeEnum.ParagraphCta:
        return CtaComponentResolver.getInstance();
      case ParagraphDividerData.TypeEnum.ParagraphDivider:
        return DividerComponentResolver.getInstance();
      case ParagraphFormContentData.TypeEnum.ParagraphFormContent:
        return FormComponentResolver.getInstance();
      case ParagraphFreeTextData.TypeEnum.ParagraphFreeText:
        return FreeTextComponentResolver.getInstance();
      case ParagraphHeroSliderData.TypeEnum.ParagraphHeroSlider:
        return HeroSliderComponentResolver.getInstance();
      case ParagraphLogoSliderData.TypeEnum.ParagraphLogoSlider:
          return LogoSliderComponentResolver.getInstance();

      case ParagraphNewsTickerItemData.TypeEnum.ParagraphNewsTickerItem:
          return NewsTickerComponentResolver.getInstance();    
      case ParagraphImageData.TypeEnum.ParagraphImage:
        return MediaComponentResolver.getInstance();
      case ParagraphMemberData.TypeEnum.ParagraphMember:
        return MemberComponentResolver.getInstance();
      case ParagraphSliderData.TypeEnum.ParagraphSlider:
        return MediaSliderComponentResolver.getInstance();
      case ParagraphMultipleColumnData.TypeEnum.ParagraphMultipleColumn:
        return MultipleColumnComponentResolver.getInstance();
      case ParagraphSmallColumnModuleData.TypeEnum.ParagraphSmallColumnModule:
        return SmallColumnModuleComponentResolver.getInstance();
      case ParagraphRelatedItemsData.TypeEnum.ParagraphRelatedItems:
        return RelatedItemsComponentResolver.getInstance();
      case ParagraphRelatedSolutionsData.TypeEnum.ParagraphRelatedSolutions:
        return RelatedSolutionsComponentResolver.getInstance();
      case ParagraphSectionHeadlineData.TypeEnum.ParagraphSectionHeadline:
        return SectionHeadlineComponentResolver.getInstance();
      case ParagraphTeamData.TypeEnum.ParagraphTeam:
        return TeamComponentResolver.getInstance();
      case ParagraphTestimonialSliderData.TypeEnum.ParagraphTestimonialSlider:
        return TestimonialSliderComponentResolver.getInstance();
      case ParagraphTextMarginalData.TypeEnum.ParagraphTextMarginal:
        return TextMarginalComponentResolver.getInstance();
      case ParagraphTextMediaBoxData.TypeEnum.ParagraphTextMediaBox:
        return TextMediaBoxComponentResolver.getInstance();
      case ParagraphWidgetData.TypeEnum.ParagraphWidget:
        return WidgetComponentResolver.getInstance();
     // case Nar
     case ParagraphNavigationWidgetData.TypeEnum.ParagraphNavigationWidget:
        return NavigationWidgetComponentResolver.getInstance();
      default:
        return null;
    }
  }
}
